import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Truck, Wrench, BadgeCheck, ShieldCheck, Settings, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import SectionHeader from "@/components/SectionHeader";
import ModelCard from "@/components/ModelCard";
import FinalCTA from "@/components/FinalCTA";
import SafeImage from "@/components/SafeImage";
import { MODELS, BENEFITS, USE_CASES, PROCESS, IMAGES, GALLERY, SITE } from "@/data/content";

const BENEFIT_ICONS = [Truck, ShieldCheck, Hammer, BadgeCheck, Settings, Wrench];

const Home = () => {
  return (
    <>
      <SEO />

      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <div className="container-rb hero__grid">
          <div>
            <span className="hero__eyebrow">RB TIM — Kontejneri</span>
            <h1>Standardni kontejneri za brzu i praktičnu upotrebu</h1>
            <p className="hero__lead">
              RB TIM izrađuje standardne kontejnere za stanovanje, kancelarije,
              gradilišta, sanitarne potrebe, portirnice i pomoćne objekte.
              Praktično rešenje kada vam je potreban funkcionalan prostor u
              kratkom roku.
            </p>
            <div className="hero__actions">
              <Link to="/modeli-kontejnera" className="btn btn-primary" data-testid="hero-cta-models">
                Pogledajte modele <ArrowRight size={16} />
              </Link>
              <Link to="/kontakt" className="btn btn-outline" data-testid="hero-cta-contact">
                Pošaljite upit
              </Link>
            </div>
            <div className="hero__stats">
              <div>
                <div className="hero__stat-num">7</div>
                <div className="hero__stat-label">Modela kontejnera</div>
              </div>
              <div>
                <div className="hero__stat-num">Brza</div>
                <div className="hero__stat-label">Izrada i isporuka</div>
              </div>
              <div>
                <div className="hero__stat-num">Po meri</div>
                <div className="hero__stat-label">U skladu sa namenom</div>
              </div>
            </div>
          </div>
          <div className="hero__media" data-testid="hero-media">
            <SafeImage src={IMAGES.hero} alt="Standardni kontejner RB TIM" label="Standardni kontejner" />
            <span className="hero__badge">
              <span className="hero__badge-dot" />
              Standardni kontejner
            </span>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section section--gray" data-testid="home-intro">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Kontejneri za različite namene"
            title="Funkcionalan prostor za posao, teren i stanovanje"
            lead="Bilo da vam je potreban prostor za rad, smeštaj, gradilište, portirnicu ili pomoćni objekat, RB TIM nudi standardna kontejnerska rešenja koja se brzo izrađuju i lako prilagođavaju osnovnim potrebama korisnika."
          />
        </div>
      </section>

      {/* MODELS GRID */}
      <section className="section" data-testid="home-models">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Modeli"
            title="Modeli kontejnera"
            lead="Pregled naših standardnih modela. Kliknite na bilo koji model za detaljnije informacije."
          />
          <div className="grid-3">
            {MODELS.map((m) => (
              <ModelCard key={m.slug} model={m} />
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="section section--gray" data-testid="home-benefits">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Zašto RB TIM?"
            title="Zašto RB TIM?"
            lead="Fokusirani smo na praktična rešenja, brzu izradu i kontejnere koji odgovaraju realnim potrebama kupaca."
          />
          <div className="grid-3">
            {BENEFITS.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div key={b.title} className="benefit-card" data-testid={`benefit-${i}`}>
                  <div className="benefit-card__icon">
                    <Icon size={22} />
                  </div>
                  <h3>{b.title}</h3>
                  <p>{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="section" data-testid="home-usecases">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Primena"
            title="Gde se koriste naši kontejneri?"
            lead="Kontejneri RB TIM se koriste na različitim lokacijama i za različite namene."
          />
          <div className="usecase-grid">
            {USE_CASES.map((u, i) => (
              <div key={u} className="usecase" data-testid={`usecase-${i}`}>
                {u}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section section--gray" data-testid="home-process">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Proces"
            title="Kako funkcioniše poručivanje?"
            lead="Od upita do isporuke — jednostavan proces u 5 koraka."
          />
          <div className="steps">
            {PROCESS.map((p, i) => (
              <div key={p.title} className="step" data-testid={`process-step-${i}`}>
                <div className="step__num">{i + 1}</div>
                <h4>{p.title}</h4>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="section" data-testid="home-gallery-preview">
        <div className="container-rb">
          <SectionHeader
            eyebrow="Galerija"
            title="Primeri izvedenih kontejnera"
            lead="Pogledajte nekoliko primera iz naše galerije."
          />
          <div className="gallery-grid">
            {GALLERY.slice(0, 6).map((g, i) => (
              <div key={i} className="gallery-item" data-testid={`home-gallery-${i}`}>
                <SafeImage src={g.src} alt={g.label} label={g.label} loading="lazy" />
                <span className="gallery-item__label">{g.label}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 28, textAlign: "center" }}>
            <Link to="/galerija" className="btn btn-outline" data-testid="home-gallery-cta">
              Pogledajte celu galeriju <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA />

      {/* Phone strip */}
      <section className="section-tight section--blue" data-testid="home-phone-strip">
        <div className="container-rb" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h3 style={{ color: "#fff", margin: 0 }}>Imate pitanje? Pozovite nas.</h3>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.9)" }}>Brz odgovor i jasna ponuda za vašu namenu.</p>
          </div>
          <a href={SITE.phoneHref} className="btn btn-white" data-testid="home-phone-cta">
            <Phone size={16} /> {SITE.phone}
          </a>
        </div>
      </section>
    </>
  );
};

export default Home;
